import { Dispatch, ForwardedRef, forwardRef, SetStateAction, useCallback, useEffect, useState } from 'react';
import { getDisableClickAwayProps, IconName } from '@optimus-web/core';
import { DataTestId, FeatureFlag, TextKey } from '@mews-commander/enums';
import { useFeatureFlag, useLocalizeText } from '@mews/framework';
import { KeyValue } from '@mews/enums';
import { AssistantSuggestion } from '@mews-commander/data-access';
import { getCurrentEnterpriseName } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { useCommanderSelector } from '@mews-commander/utils';
import { NavbarIconButton } from '../../../components';
import { SearchStateProps } from '../Assistant';
import { AssistantSearchListItemProps } from '../drawer/hooks/useAssistantSearchResultListMap';
import { AssistantListItemSelectionStateProps } from '../drawer/hooks/useAssistantListItemSelectionHandler';
import { useAssistantAutocompleteQuery } from '../query/useAssistantAutocompleteQuery';
import { SearchBarContainer, SearchIcon, SearchIconContainer, SearchInputContainer } from './styles';
import { Autocomplete } from './autocomplete/Autocomplete';
import { SearchInputRightElement } from './searchInputRightElement';
import { ExtendedTextInput } from './extentedTextInput/ExtendedTextInput';
import { MqlTriggerCharacter } from './Mql';
import { useKeywordColors } from './hooks/useKeywordColors';
import { containsKeyword, convertToMql, mergeText, startsWithMqlTriggerCharacter } from './MqlHelper';

interface Props {
    mobileSearchOpened?: boolean;
    mobileOrTablet: boolean;
    focused: boolean;
    loading: boolean;
    enterpriseIds?: Array<string>;
    handleBackClick: () => void;
    handleSearch: (searchTerm: string, mqlSearch?: string) => void;
    showRecent?: boolean;
    showAutocomplete: boolean;
    setSearchState: Dispatch<SetStateAction<SearchStateProps>>;
    searchTerm: string;
    favoriteListItems: Array<AssistantSearchListItemProps>;
    recentSearchTerm: string;
    setListItemSelectionState: Dispatch<SetStateAction<AssistantListItemSelectionStateProps>>;
    selectedFavoritePrompt?: AssistantSearchListItemProps;
    setSelectedFavoritePrompt: Dispatch<SetStateAction<AssistantSearchListItemProps | undefined>>;
}

export const SearchBar = forwardRef((props: Props, searchRef: ForwardedRef<HTMLInputElement>) => {
    const {
        mobileOrTablet,
        focused,
        loading,
        enterpriseIds,
        handleBackClick,
        handleSearch,
        showRecent,
        showAutocomplete,
        setSearchState,
        searchTerm,
        recentSearchTerm,
        favoriteListItems,
        setListItemSelectionState,
        selectedFavoritePrompt,
        setSelectedFavoritePrompt,
    } = props;
    const assistantAutocompleteEnabled = useFeatureFlag(FeatureFlag.assistantAutocompleteEnabled);
    const [input, setInput] = useState('');
    const [mqlInput, setMqlInput] = useState('');
    const [isMql, setIsMql] = useState(false);
    const [keywordSelectedIndex, setKeywordSelectedIndex] = useState(-1);
    const [selectedKeyword, setSelectedKeyword] = useState<AssistantSuggestion>();
    const enterpriseName = useCommanderSelector<string>(getCurrentEnterpriseName);
    const placeHolder = useLocalizeText()({ textKey: TextKey.SearchInType, substitutes: { type: enterpriseName } });

    const keywordColors = useKeywordColors(input, enterpriseIds ?? [], assistantAutocompleteEnabled);

    const { data: autocomplete } = useAssistantAutocompleteQuery(searchTerm, enterpriseIds ?? [], true);
    const { allSuggestions } = autocomplete;
    const handleSetIsMql = useCallback(
        (_isMql: boolean) => window.setTimeout(() => setIsMql(assistantAutocompleteEnabled ? _isMql : false), 50),
        [setIsMql, assistantAutocompleteEnabled]
    );

    useEffect(() => {
        if (!searchTerm && !focused) {
            setInput('');
        }
    }, [searchTerm, focused]);

    useEffect(() => {
        if (recentSearchTerm) {
            setInput(recentSearchTerm);
            handleSearch(recentSearchTerm);
        }
    }, [handleSearch, recentSearchTerm]);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const { value } = e.target;
            const autoComplete = assistantAutocompleteEnabled && (startsWithMqlTriggerCharacter(input) || isMql);
            setInput(value);
            setKeywordSelectedIndex(-1);
            setSelectedKeyword(undefined);
            if (!autoComplete && value !== MqlTriggerCharacter) {
                handleSearch(value);
            } else {
                setSearchState((prev) => ({
                    ...prev,
                    autoComplete: true,
                }));
            }
        },
        [assistantAutocompleteEnabled, input, isMql, handleSearch, setSearchState]
    );

    const setKeyword = useCallback(
        (newKeyword: AssistantSuggestion | undefined) => {
            if (newKeyword) {
                const hasKeyword = containsKeyword(input, keywordColors);
                if (hasKeyword) {
                    const text = mergeText(input, newKeyword.text);
                    const mqlText: string = convertToMql(text, newKeyword);
                    setInput(text);
                    setMqlInput(mqlText);
                } else {
                    setInput(`${MqlTriggerCharacter}${newKeyword.text}`);
                }
                setKeywordSelectedIndex(0);
            }
        },
        [input, keywordColors, setKeywordSelectedIndex, setInput]
    );

    const handleSearchInputKeyUp = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            const autoComplete = assistantAutocompleteEnabled && (startsWithMqlTriggerCharacter(input) || isMql);
            if (autoComplete) {
                let handled = false;
                if (e.key === KeyValue.Enter && input.length > 0) {
                    const searchTerms = startsWithMqlTriggerCharacter(input) ? input.substring(1) : input;
                    handleSearch(searchTerms, mqlInput);
                    handled = true;
                } else if (e.key === KeyValue.ArrowDown) {
                    const index = keywordSelectedIndex + 1 > allSuggestions.length - 1 ? 0 : keywordSelectedIndex + 1;
                    setKeywordSelectedIndex(index);
                    handled = true;
                } else if (e.key === KeyValue.ArrowUp) {
                    const index = keywordSelectedIndex - 1 < 0 ? allSuggestions.length - 1 : keywordSelectedIndex - 1;
                    setKeywordSelectedIndex(index);
                    handled = true;
                } else if (e.key === KeyValue.ArrowRight) {
                    setKeyword(selectedKeyword);
                    handled = true;
                }

                if (handled) {
                    e.stopPropagation();
                    e.preventDefault();
                }
            }
        },
        [
            assistantAutocompleteEnabled,
            input,
            isMql,
            keywordSelectedIndex,
            allSuggestions,
            handleSearch,
            setKeyword,
            selectedKeyword,
            mqlInput,
        ]
    );

    const handleSearchInputKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            const autoComplete = assistantAutocompleteEnabled && (startsWithMqlTriggerCharacter(input) || isMql);
            if (autoComplete && e.key === KeyValue.Tab) {
                e.stopPropagation();
                e.preventDefault();
                setKeyword(selectedKeyword);
            } else if (autoComplete && e.key === KeyValue.ArrowUp) {
                e.stopPropagation();
                e.preventDefault();
            }
        },
        [assistantAutocompleteEnabled, input, isMql, selectedKeyword, setKeyword]
    );

    const handleOnFocus = useCallback(() => {
        setSearchState((prev) => ({
            ...prev,
            focused: true,
        }));
    }, [setSearchState]);

    const handleKeywordSelection = useCallback((keyword: AssistantSuggestion) => setKeyword(keyword), [setKeyword]);

    const handleClearSelection = () => {
        setInput('');
        handleSearch('');
        setListItemSelectionState((prev) => ({
            ...prev,
            selectedIndex: 0,
            selectedTabIndex: 0,
            isTabNavigation: false,
        }));
    };

    return (
        <SearchBarContainer isMobile={mobileOrTablet} focused={focused}>
            {mobileOrTablet ? <NavbarIconButton icon={IconName.ArrowLeft} onClick={handleBackClick} /> : null}

            <SearchInputContainer focused={focused} isLoading={loading} {...getDisableClickAwayProps()}>
                <SearchIconContainer focused={focused}>
                    <SearchIcon size="small" variant="basic" />
                </SearchIconContainer>

                <ExtendedTextInput
                    ref={searchRef}
                    focused={focused}
                    enterpriseIds={enterpriseIds}
                    data-test-id={DataTestId.AssistantNavbarSearch}
                    placeholder={placeHolder}
                    input={input}
                    keywordSelectedIndex={keywordSelectedIndex}
                    keywordColors={keywordColors}
                    onChange={handleChange}
                    onFocus={handleOnFocus}
                    onKeyUp={handleSearchInputKeyUp}
                    onKeyDown={handleSearchInputKeyDown}
                    setIsMql={handleSetIsMql}
                    handleClearSelection={handleClearSelection}
                />
                <SearchInputRightElement
                    searchTerm={searchTerm}
                    showRecent={showRecent}
                    loading={loading}
                    favoriteListItems={favoriteListItems}
                    selectedFavoritePrompt={selectedFavoritePrompt}
                    setSelectedFavoritePrompt={setSelectedFavoritePrompt}
                />
            </SearchInputContainer>
            {assistantAutocompleteEnabled && showAutocomplete && (
                <Autocomplete
                    searchTerm={input}
                    isMql={isMql}
                    focused={focused}
                    selectedIndex={keywordSelectedIndex}
                    setSelectedSuggestion={setSelectedKeyword}
                    handleSuggestionSelection={handleKeywordSelection}
                    enterpriseIds={enterpriseIds}
                />
            )}
        </SearchBarContainer>
    );
});
